import { computed, effect, inject, Injectable } from '@angular/core';
import { SonaAuthApiFacade, SonaUsageFacade } from '@sona/angular-auth-api';
import { useI18n } from '@sona/angular-util-common';
import { ENVIRONMENT } from '@sona/angular-util-environment';
import { Subscription } from '@sona/shared-model-common';
import { omitBy } from 'lodash';

declare global {
    interface Window {
        Featurebase: any;
    }
}

@Injectable()
export class Featurebase {

    private readonly _environment = inject(ENVIRONMENT);
    private readonly _usageFacade = inject(SonaUsageFacade);
    private readonly _i18n = useI18n();
    private readonly _language = computed(() => this._i18n().language);
    private readonly _user = inject(SonaAuthApiFacade).user;
    private readonly _organizationId = inject(SonaAuthApiFacade).organizationId;
    private readonly _organizationName = inject(SonaAuthApiFacade).organizationName;
    private readonly _userHash = inject(SonaAuthApiFacade).featurebaseIdentityVerificationHash;
    private readonly _companyHash = inject(SonaAuthApiFacade).organizationFeaturebaseIdentityVerificationHash;

    private readonly _bootData = computed(() => {
        const user = this._user();

        if (!user) {
            return undefined;
        }

        return {
            appId: this._environment.featurebase.appId,
            theme: 'light',
            language: this._language(),
            email: user.email,
            userId: user.id,
            userHash: this._userHash(),
            alignment: 'right',
            horizontalPadding: 20,
            verticalPadding: 20
        };
    });
    private readonly _identifyData = computed(() => {
        const user = this._user();
        const userFeaturebaseHash = this._userHash();
        const organizationFeaturebaseHash = this._companyHash();

        if (!user) {
            return undefined;
        }

        const usage = this._usageFacade.hasValue() ? this._usageFacade.usage() : undefined;
        const limits = this._usageFacade.hasValue() ? this._usageFacade.limits() : undefined;
        const subscription = usage?.subscription;

        return {
            organization: 'siteboard',
            userId: user.id,
            userHash: userFeaturebaseHash,
            name: user.email,
            email: user.email,
            companies: [
                {
                    id: this._organizationId(),
                    companyHash: organizationFeaturebaseHash,
                    name: this._organizationName(),
                    monthlySpend: this._estimateMrr(subscription),
                    customFields: omitBy({
                        plan: subscription?.plan,
                        status: subscription?.status,
                        credits: limits?.credits?.toString(),
                        domains: limits?.domains?.toString(),
                        creditsUsed: usage?.creditsUsed?.toString(),
                        starterAudits: usage?.starterAudits?.toString()
                    }, (v) => v == null)
                }
            ]
        };
    });

    constructor() {
        if (typeof window.Featurebase !== 'function') {
            window.Featurebase = function () {
                // eslint-disable-next-line prefer-rest-params
                (window.Featurebase.q = window.Featurebase.q || []).push(arguments);
            };
        }

        // Update the language of the messenger & feedback widgets
        effect(() => {
            const language = this._language();

            this._featurebase('setLanguage', language);

            this._featurebase('initialize_feedback_widget', {
                organization: 'siteboard',
                theme: 'light',
                placement: 'right',
                locale: language === 'en' ? 'en-US' : language,
                metadata: {
                    version: this._environment.version,
                    environment: this._environment.name
                }
            });
        });

        const bootRef = effect(() => {
            const bootData = this._bootData();

            if (!bootData) {
                return;
            }

            this._featurebase('boot', bootData);
            bootRef.destroy();
        }, { manualCleanup: true });

        // Identify the user
        effect(() => {
            const data = this._identifyData();

            if (!data) {
                return;
            }

            this._featurebase('identify', data);
        });
    }

    openMessenger(): void {
        this._featurebase('show', 'messages');
    }

    private _featurebase(type: string, data: unknown, callback?: (err: any) => void): void {
        window.Featurebase(type, data, callback);
    }

    private _estimateMrr(subscription: Subscription | undefined): number {
        if (!subscription || subscription.status !== 'active') {
            return 0;
        }

        switch (subscription.plan) {
            case 'free':
            case 'pilot':
                return 0;
            case 'pro':
                return 75;
            case 'agency':
                return 225;
            case 'enterprise':
                return 500;
        }
    }
}
